import { useCallback } from 'react'

import { useNavigate } from 'react-router'

import PlusIcon from '@/assets/icons/icon-plus.svg'
import { Page } from '@/components/layout/page/page.tsx'
import { Button } from '@/components/ui/button.tsx'
import { Separator } from '@/components/ui/separator.tsx'
import { SettingsPageTitle } from '@/modules/creator-settings/SettingsPageTitle'
import { RebillMessagesTable } from '@/modules/creator-settings/rebill-messages/components/tables/rebill-messages-table.tsx'
import { useToggleRebillMessageMutation } from '@/modules/creator-settings/rebill-messages/hooks/use-toggle-rebill-message.ts'
import { useUpdateRebillMessageHoursMutation } from '@/modules/creator-settings/rebill-messages/hooks/use-update-rebill-message-hours.ts'
import { useUpdateRebillMessageMinutesMutation } from '@/modules/creator-settings/rebill-messages/hooks/use-update-rebill-message-minutes.ts'
import { useSettingsContext } from '@/modules/creator-settings/state/SettingsProvider.tsx'
import { SettingsOption } from '@/modules/creator-settings/ui/SettingsOption.tsx'
import { TimeInputSection } from '@/modules/expiring-fans/components/time-input-section'
import { useDebouncedProperty } from '@/utils/useDebouncedProperty.ts'

const minTimeValue = 1
const maxMinutesValue = 60
const maxHoursValue = 48
const DELAY_FOR_TIME_UPDATE = 1500

export const RebillMessagesPage = () => {
  const redirect = useNavigate()
  const toggleRebillMessageMutation = useToggleRebillMessageMutation()
  const updateRebillMessageMinutesMutation =
    useUpdateRebillMessageMinutesMutation()
  const updateRebillMessageHoursMutation = useUpdateRebillMessageHoursMutation()
  const { autoMessageRebilledFansSettings } = useSettingsContext()

  const toggleRebill = useCallback(
    () => {
      toggleRebillMessageMutation.mutate(
        !autoMessageRebilledFansSettings!.enabled,
      )
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [autoMessageRebilledFansSettings!.enabled, toggleRebillMessageMutation],
  )

  const [delayMinutesValue, setDelayMinutesValue] = useDebouncedProperty(
    autoMessageRebilledFansSettings!.sendAfterMinutes!,
    updateRebillMessageMinutesMutation.mutate,
    DELAY_FOR_TIME_UPDATE,
  )

  const [delayHoursValue, setDelayHoursValue] = useDebouncedProperty(
    autoMessageRebilledFansSettings!.minHoursSinceLastMessage!,
    updateRebillMessageHoursMutation.mutate,
    DELAY_FOR_TIME_UPDATE,
  )

  const handleRedirect = () => {
    redirect('create')
  }

  return (
    <Page>
      <SettingsPageTitle
        title="Rebill Messages"
        toggleName="enable-follow-up-message"
        onChange={toggleRebill}
        enabled={autoMessageRebilledFansSettings!.enabled!}
        description={
          <span>
            Automatically sends one or multiple messages to fans who renew their
            subscription
          </span>
        }
      />
      {autoMessageRebilledFansSettings!.enabled && (
        <div>
          <Separator className="mt-8" />
          <TimeInputSection
            title="Set Time"
            description="The number of minutes after the fan’s subscription rebill is activated until they receive the rebill message."
            unit="Minutes"
            min={minTimeValue}
            max={maxMinutesValue}
            textClassName="max-w-[134px]"
            value={delayMinutesValue}
            onChange={setDelayMinutesValue}
            disabled={updateRebillMessageMinutesMutation.isLoading}
          />
          <Separator />
          <TimeInputSection
            title="Time Limitation"
            description="Only send messages if the last chat is older than the specified amount of time"
            unit="Hours"
            min={minTimeValue}
            max={maxHoursValue}
            textClassName="max-w-[134px]"
            value={delayHoursValue}
            onChange={setDelayHoursValue}
            disabled={updateRebillMessageHoursMutation.isLoading}
          />
          <Separator />
          <SettingsOption
            title="Messages"
            description="Messages that will be sent automatically"
            mobileVertical
            biggerTitle
          >
            <Button onClick={handleRedirect}>
              <PlusIcon /> Create Message
            </Button>
          </SettingsOption>

          <RebillMessagesTable />
        </div>
      )}
    </Page>
  )
}
